import React, { useMemo } from "react";
import Button from "@mui/material/Button";
import Grid2 from "@mui/material/Unstable_Grid2";
import InfoIcon from "@mui/icons-material/Info";
import { useAppDispatch, useAppSelector } from "../../framework/customStore";
import {
    clearWorkTimeReportFilters as clearFilters,
    setWorkTimeReportSelectedCostCenters as setSelectedCostCenters,
    setWorkTimeReportSelectedCustomers as setSelectedCustomers,
    setWorkTimeReportSelectedEmployeeGroups as setSelectedEmployeeGroups,
    setWorkTimeReportSelectedEmployees as setSelectedEmployees,
    setWorkTimeReportSelectedVehicles as setSelectedVehicles,
    setWorkTimeReportSelectedWorkTimeTypes as setSelectedWorkTimeTypes,
    setWorkTimeReportTimeRange as setTimeRange,
} from "../../store/reportOptionsSlice";
import { ICostCenterItem } from "../../models/costCenter/costCenterItem";
import { Base } from "../../framework/base";
import { VehicleListItemDto } from "../../models/transport/vehicle";
import { EmployeeListItemDto } from "../../models/employee/employee";
import { ICustomerItem } from "../../models/customer/customerItem";
import { useTranslation } from "react-i18next";
import { WorkTimeType } from "../../models/workShitTimeSlot/workTimeType";
import { MuiDateRangePicker } from "../framework/muiDateRangePicker";
import {
    DateRangeShortcutButton,
    ShortcutOption,
} from "../framework/dateRangeShortcuts";
import {
    AutocompleteOption,
    AutocompleteValue,
    AutocompleteWrapper,
} from "../framework/muiAutocomplete";
import { AnyAction } from "@reduxjs/toolkit";
import { employeeGroupListItemDto } from "../../models/employeeGroup/employeeGroup";
import { ReportSection } from "./ReportSection";
import { HtmlTooltip } from "../framework/tooltip";
import { useAppOptionValueEquals } from "../../hooks/useAppOption";
import { OptionWorkTimeReportCalculationEnabled } from "../../models/common/consts";

export const WorkTimeReportFilters = ({
    vehicles,
    costCenters,
    employees,
    employeeGroups,
    customers,
    workTimeTypes,
}: {
    vehicles: VehicleListItemDto[];
    costCenters: ICostCenterItem[];
    employees: EmployeeListItemDto[];
    employeeGroups: employeeGroupListItemDto[];
    customers: ICustomerItem[];
    workTimeTypes: WorkTimeType[];
}) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const [reportCalculationEnabled] = useAppOptionValueEquals(
        OptionWorkTimeReportCalculationEnabled,
        true
    );

    const timeRange = useAppSelector(
        (state) => state.reportOptions.workTimeFilters.timeRange
    );
    const selectedVehicles = useAppSelector(
        (state) => state.reportOptions.workTimeFilters.selectedVehicles
    );
    const selectedCostCenters = useAppSelector(
        (state) => state.reportOptions.workTimeFilters.selectedCostCenters
    );
    const selectedEmployees = useAppSelector(
        (state) => state.reportOptions.workTimeFilters.selectedEmployees
    );
    const selectedEmployeeGroups = useAppSelector(
        (state) => state.reportOptions.workTimeFilters.selectedEmployeeGroups
    );
    const selectedCustomers = useAppSelector(
        (state) => state.reportOptions.workTimeFilters.selectedCustomers
    );
    const selectedWorkTimeTypes = useAppSelector(
        (state) => state.reportOptions.workTimeFilters.selectedWorkTimeTypes
    );

    const costCenterOptionsWithGroups = useMemo(() => {
        if (!!costCenters) {
            const withGroup = costCenters
                .map((i) => ({
                    id: i.id,
                    title: i.name,
                    group: i.customerName,
                }))
                .sort((a, b) =>
                    // Sort first so internal cost centers are first.
                    Base.stringCompare(a.group, b.group)
                )
                .map((i) => ({
                    ...i,
                    // Add label for internal cost centers after sorting.
                    group: i.group || t("costCenter.internal"),
                }));
            return withGroup;
        }
        return [];
    }, [costCenters]);

    return (
        <ReportSection id="filters" header={t("reporting.filters")}>
            <Grid2 container spacing={2}>
                <Grid2 container xs={12}>
                    <Grid2 xs="auto">
                        <MuiDateRangePicker
                            size="small"
                            label={t("reporting.dateRange")}
                            value={timeRange}
                            onChange={(val) => dispatch(setTimeRange(val))}
                        />
                    </Grid2>
                    <Grid2>
                        <DateRangeShortcutButton
                            size="large"
                            option={ShortcutOption.PreviousWeek}
                            dateRange={timeRange}
                            setDateRange={(val) => dispatch(setTimeRange(val))}
                        />
                    </Grid2>
                    <Grid2>
                        <DateRangeShortcutButton
                            size="large"
                            option={ShortcutOption.NextWeek}
                            dateRange={timeRange}
                            setDateRange={(val) => dispatch(setTimeRange(val))}
                        />
                    </Grid2>
                    <Grid2>
                        <DateRangeShortcutButton
                            size="large"
                            option={ShortcutOption.PreviousMonth}
                            dateRange={timeRange}
                            setDateRange={(val) => dispatch(setTimeRange(val))}
                        />
                    </Grid2>
                    <Grid2>
                        <DateRangeShortcutButton
                            size="large"
                            option={ShortcutOption.NextMonth}
                            dateRange={timeRange}
                            setDateRange={(val) => dispatch(setTimeRange(val))}
                        />
                    </Grid2>
                    <Grid2
                        xsOffset="auto"
                        alignItems="self-end"
                        alignContent="center"
                    >
                        <HtmlTooltip
                            title={
                                reportCalculationEnabled
                                    ? t(
                                        "reporting.workTimeReportInfoCalculationEnabled"
                                    )
                                    : t("reporting.workTimeReportInfo")
                            }
                            tooltipSx={{ whiteSpace: "pre-wrap" }}
                        >
                            <InfoIcon color="primary" />
                        </HtmlTooltip>
                    </Grid2>
                </Grid2>
                <Grid2 container xs={12} spacing={2}>
                    <FilterColumn>
                        <AutocompleteFilter
                            label={t("reporting.employeeGroups")}
                            value={selectedEmployeeGroups}
                            setValue={setSelectedEmployeeGroups}
                            options={employeeGroups.map((g) => ({
                                id: g.id,
                                title: g.name,
                            }))}
                        />
                    </FilterColumn>
                    <FilterColumn>
                        <AutocompleteFilter
                            label={t("reporting.employees")}
                            value={selectedEmployees}
                            setValue={setSelectedEmployees}
                            options={(employees || [])
                                .filter(
                                    (e) =>
                                        selectedEmployeeGroups.length === 0 ||
                                        selectedEmployeeGroups.includes(
                                            e.salaryGroupId
                                        )
                                )
                                .map((g) => ({
                                    id: g.id,
                                    title: g.fullName,
                                }))}
                        />
                    </FilterColumn>
                    <FilterColumn>
                        <AutocompleteFilter
                            label={t("reporting.fleet")}
                            value={selectedVehicles}
                            setValue={setSelectedVehicles}
                            options={vehicles.map((g) => ({
                                id: g.id,
                                title: `${g.registerNumber} - ${g.brand}`,
                            }))}
                        />
                    </FilterColumn>
                    <FilterColumn>
                        <AutocompleteFilter
                            label={t("reporting.customers")}
                            value={selectedCustomers}
                            setValue={setSelectedCustomers}
                            options={customers.map((g) => ({
                                id: g.id,
                                title: g.name,
                            }))}
                        />
                    </FilterColumn>

                    <FilterColumn>
                        <AutocompleteFilter
                            label={t("reporting.costCenters")}
                            value={selectedCostCenters}
                            setValue={setSelectedCostCenters}
                            options={costCenterOptionsWithGroups}
                            groupBy={(option) => option.group}
                        />
                    </FilterColumn>

                    <FilterColumn>
                        <AutocompleteFilter
                            label={t("reporting.workTimeTypes")}
                            value={selectedWorkTimeTypes}
                            setValue={setSelectedWorkTimeTypes}
                            options={workTimeTypes.map((t) => ({
                                id: t.workTimeTypeId,
                                title: t.name,
                            }))}
                        />
                    </FilterColumn>
                    <Grid2 xsOffset="auto" alignItems="self-end">
                        <Button
                            color="primary"
                            sx={{
                                height: 40,
                            }}
                            onClick={() => dispatch(clearFilters())}
                            variant={"contained"}
                        >
                            {t("common.clear")}
                        </Button>
                    </Grid2>
                </Grid2>
            </Grid2>
        </ReportSection>
    );
};

interface AutocompleteFilterProps {
    label: string;
    value: string[] | null;
    options: AutocompleteOption[];
    setValue: (value: string[]) => AnyAction;
    groupBy?: (o: AutocompleteOption) => string;
}

const AutocompleteFilter = ({
    label,
    value,
    options,
    setValue,
    groupBy,
}: AutocompleteFilterProps) => {
    const dispatch = useAppDispatch();

    return (
        <AutocompleteWrapper
            label={label}
            size="small"
            value={value ?? []}
            multiple={true}
            limitTags={2}
            fullWidth={true}
            options={options}
            groupBy={groupBy}
            onChange={(value: AutocompleteValue | null) =>
                dispatch(
                    setValue(
                        ((value as AutocompleteOption[]) ?? []).map((o) => o.id)
                    )
                )
            }
        />
    );
};

const FilterColumn = ({ children }: { children: JSX.Element }) => (
    <Grid2 xs={12} sm={6} md={4} lg={2} xl={true}>
        {children}
    </Grid2>
);
